import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";

export function DeleteEntity({
  kind,
  id,
  name,
  onDeleted,
  open,
  onClose,
}: {
  kind: "person" | "company";
  id: string;
  name: string;
  onDeleted: () => void;
  open: boolean;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const { toast } = useToast();
  async function remove() {
    if (busy) return;
    setBusy(true);
    try {
      await unwrap(
        apiClient().DELETE(kind === "person" ? "/people/{id}" : "/companies/{id}", {
          params: { path: { id } },
        }),
      );
      await client.cancelQueries();
      onClose();
      onDeleted();
      client.removeQueries({ queryKey: [kind, id] });
      void client.invalidateQueries();
      toast({
        variant: ToastVariant.SUCCESS,
        header: `${kind === "person" ? "Person" : "Company"} deleted`,
        description: name,
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <ConfirmAction
      isOpen={open}
      header={`Delete ${name}?`}
      description={
        kind === "person"
          ? "This person and their signals will leave working views. Collection will not recreate this person. Existing history is kept."
          : "This company will be removed and unlinked from its people. The people and existing history are kept."
      }
      label={`Delete ${kind}`}
      isDestructive
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      onConfirm={async () => remove()}
    />
  );
}
